import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { problemOf } from '../api/client'
import { useTemplateMutations, type TemplateInput } from '../api/admin'
import { useTemplates } from '../api/hooks'
import type { FormKind, TemplateSummary } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Badge, Empty, Modal } from '../components/ui'

export function errMsg(e: unknown): string {
  const p = problemOf(e)
  return p?.detail ?? p?.title ?? (e instanceof Error ? e.message : String(e))
}

export default function TemplatesPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const templates = useTemplates()
  const m = useTemplateMutations()
  const [modal, setModal] = useState<'create' | 'import' | { clone: TemplateSummary } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const canManage = user?.roles.some((r) => r.role === 'TenantAdmin' || r.role === 'SuperAdmin') ?? false

  const [form, setForm] = useState<TemplateInput>({ code: '', name: '', kind: 'Checklist', description: '', instanceCodePrefix: '' })
  const [json, setJson] = useState('')
  const [cloneCode, setCloneCode] = useState('')
  const [cloneName, setCloneName] = useState('')

  const run = async (fn: () => Promise<TemplateSummary>) => {
    setError(null)
    try {
      const created = await fn()
      setModal(null)
      navigate(`/templates/${created.id}`)
    } catch (e) {
      setError(errMsg(e))
    }
  }

  return (
    <div className="stack">
      <div className="row between wrap">
        <h2>{t('templates.title')}</h2>
        {canManage ? (
          <div className="row">
            <button className="secondary" onClick={() => { setJson(''); setModal('import') }}>{t('templates.importJson')}</button>
            <button onClick={() => { setForm({ code: '', name: '', kind: 'Checklist', description: '', instanceCodePrefix: '' }); setModal('create') }}>{t('templates.new')}</button>
          </div>
        ) : null}
      </div>
      <p className="muted small">{t('templates.hint')}</p>
      <div className="card">
        {templates.isLoading ? <p>{t('common.loading')}</p> : !templates.data?.length ? <Empty text={t('templates.empty')} /> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>{t('templates.code')}</th><th>{t('templates.name')}</th><th>{t('templates.kind')}</th><th>{t('templates.scope')}</th><th className="num">{t('templates.version')}</th><th className="num">{t('templates.questions')}</th><th /></tr></thead>
              <tbody>
                {templates.data.map((x) => (
                  <tr key={x.id}>
                    <td><Link to={`/templates/${x.id}`}><b>{x.code}</b></Link></td>
                    <td>{x.name}{x.description ? <div className="muted small">{x.description}</div> : null}</td>
                    <td><Badge>{t(`formKind.${x.kind}`)}</Badge></td>
                    <td>{x.isGlobal ? <Badge color="#4a3aa7">{t('templates.global')}</Badge> : <Badge color="#2a78d6">{t('templates.tenant')}</Badge>}</td>
                    <td className="num">{x.currentVersionNo ? `v${x.currentVersionNo}` : <span className="muted">{t('templates.noPublished')}</span>}</td>
                    <td className="num">{x.questionCount}</td>
                    <td className="row">
                      <Link className="button secondary small-btn" to={`/templates/${x.id}`}>{canManage && !x.isGlobal ? t('templates.edit') : t('templates.view')}</Link>
                      {canManage ? <button className="secondary small-btn" onClick={() => { setCloneCode(`${x.code}-KOPYA`); setCloneName(x.name); setModal({ clone: x }) }}>{t('templates.clone')}</button> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal === 'create' ? (
        <Modal title={t('templates.new')} onClose={() => setModal(null)}>
          <label>{t('templates.code')}<input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="K-LST-7.1" /></label>
          <label>{t('templates.name')}<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label>{t('templates.kind')}
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as FormKind })}>
              <option value="Checklist">{t('formKind.Checklist')}</option>
              <option value="ScoredAudit">{t('formKind.ScoredAudit')}</option>
            </select>
          </label>
          <label>{t('templates.prefix')}<input value={form.instanceCodePrefix ?? ''} onChange={(e) => setForm({ ...form, instanceCodePrefix: e.target.value })} placeholder="YSM" /></label>
          <label>{t('templates.description')}<textarea rows={2} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
          <p className="muted small">{t('templates.defaultsHint')}</p>
          {error ? <p className="error">{error}</p> : null}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="secondary" onClick={() => setModal(null)}>{t('common.cancel')}</button>
            <button disabled={!form.code || !form.name || m.create.isPending} onClick={() => void run(() => m.create.mutateAsync({ ...form, instanceCodePrefix: form.instanceCodePrefix || undefined, description: form.description || undefined }))}>{t('templates.create')}</button>
          </div>
        </Modal>
      ) : null}

      {modal === 'import' ? (
        <Modal title={t('templates.importJson')} onClose={() => setModal(null)} width={720}>
          <p className="muted small">{t('templates.importHint')}</p>
          <input type="file" accept=".json,application/json" onChange={(e) => { const f = e.target.files?.[0]; if (f) void f.text().then(setJson) }} />
          <textarea rows={14} value={json} onChange={(e) => setJson(e.target.value)} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} placeholder='{ "code": "...", "name": "...", "kind": "Checklist", ... }' />
          {error ? <p className="error">{error}</p> : null}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="secondary" onClick={() => setModal(null)}>{t('common.cancel')}</button>
            <button disabled={!json.trim() || m.importJson.isPending} onClick={() => void run(() => m.importJson.mutateAsync(json))}>{t('templates.import')}</button>
          </div>
        </Modal>
      ) : null}

      {modal && typeof modal === 'object' ? (
        <Modal title={`${t('templates.clone')}: ${modal.clone.code}`} onClose={() => setModal(null)}>
          <p className="muted small">{t('templates.cloneHint')}</p>
          <label>{t('templates.newCode')}<input value={cloneCode} onChange={(e) => setCloneCode(e.target.value)} /></label>
          <label>{t('templates.name')}<input value={cloneName} onChange={(e) => setCloneName(e.target.value)} /></label>
          {error ? <p className="error">{error}</p> : null}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="secondary" onClick={() => setModal(null)}>{t('common.cancel')}</button>
            <button disabled={!cloneCode || m.clone.isPending} onClick={() => void run(() => m.clone.mutateAsync({ sourceId: modal.clone.id, newCode: cloneCode, newName: cloneName }))}>{t('templates.clone')}</button>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}
